import { FC, useState, useEffect, useCallback, useMemo } from "react";

import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetCustomers,
  useSearchCustomers,
  useAssociateQuoteToService,
} from "@/_services/hooks/invoice.hooks";
import {
  Customer,
  AssociateQuoteToServiceRequest,
  Quote,
} from "@/_services/invoiceService";

import {
  CurrentOrderCard,
  QuoteSelectionCard,
  CustomerSelectionCard,
  OrderSummaryCard,
  ActionButtons,
} from "./components";
import { ProductManagementProvider } from "./context/ProductManagementContext";
import { QuoteAssociationProvider } from "./context/QuoteAssociationContext";
import { useProductManagementContext } from "./hooks/useProductManagementContext";
import { useQuoteAssociation } from "./hooks/useQuoteAssociation";
import { useQuoteAssociationConfirmDialog } from "./hooks/useQuoteAssociationConfirmDialog";

const QuoteAssociationContent: FC<{ serviceData?: any }> = ({
  serviceData,
}) => {
  const { id: serviceId } = useParams<{ id: string }>();
  const {
    handleSubmit: handleSubmitForm,
    quoteOption,
    selectedCustomer,
    selectedQuote,
    setValue,
  } = useQuoteAssociation();

  const {
    getSelectedProductsCount,
    calculateTotal,
    getSelectedProductsData,
    products,
  } = useProductManagementContext();

  const { showConfirmDialog } = useQuoteAssociationConfirmDialog();

  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [selectedCustomerData, setSelectedCustomerData] =
    useState<Customer | null>(null);
  const [selectedQuoteData, setSelectedQuoteData] = useState<Quote | null>(
    null
  );
  const [isCustomerAutoSelected, setIsCustomerAutoSelected] = useState(false);
  const [autoSelectionSource, setAutoSelectionSource] = useState<
    "quote" | "diagnostic" | null
  >(null);

  const { data: customersData, isLoading: loadingCustomers } = useGetCustomers({
    page: 1,
  });

  const { data: searchResults = [] } = useSearchCustomers(customerSearchQuery, {
    enabled: customerSearchQuery.trim().length >= 2,
  });

  const { mutate: associateQuote, isLoading: isAssociating } =
    useAssociateQuoteToService({
      handleSuccess: () => {
        toast.success(
          quoteOption === "existing"
            ? "Association avec le devis existant réussie !"
            : "Nouveau devis créé et associé avec succès !"
        );
      },
      handleError: (error) => {
        console.error("Association failed:", error);
        toast.error(
          "Erreur lors de l'association. Veuillez réessayer ou contacter l'administrateur."
        );
      },
    });

  const customers = useMemo(
    () => customersData?.items || [],
    [customersData?.items]
  );
  const filteredCustomers =
    customerSearchQuery.trim().length >= 2 ? searchResults : customers;

  const handleCustomerSelection = useCallback(
    (customerId: string) => {
      const customer =
        customers.find((c) => c.id.toString() === customerId) ||
        filteredCustomers.find((c) => c.id.toString() === customerId);

      if (customer) {
        setSelectedCustomerData(customer);
      }

      setValue("selectedCustomer", customerId);
      setCustomerSearchQuery("");
      setIsCustomerAutoSelected(false);
      setAutoSelectionSource(null);
    },
    [customers, filteredCustomers, setValue]
  );

  useEffect(() => {
    if (!selectedCustomer) {
      setSelectedCustomerData(null);
      setIsCustomerAutoSelected(false);
      setAutoSelectionSource(null);
    }
  }, [selectedCustomer]);

  // Auto-select customer from dwelling.agency.diagnosticCustomer if available
  useEffect(() => {
    if (
      serviceData?.dwelling?.agency?.diagnosticCustomer &&
      !selectedCustomer &&
      !isCustomerAutoSelected &&
      customers.length > 0
    ) {
      const dwellingCustomer = serviceData.dwelling?.agency?.diagnosticCustomer;
      // Try to find matching customer by sourceId, email, or phone
      const existingCustomer = customers.find(
        (c) =>
          (dwellingCustomer.sourceId &&
            (c.id.toString() === dwellingCustomer.sourceId ||
              c.external_reference === dwellingCustomer.sourceId)) ||
          (dwellingCustomer.emails &&
            dwellingCustomer.emails.some((email: string) =>
              c.emails.includes(email)
            )) ||
          (dwellingCustomer.phone && c.phone === dwellingCustomer.phone) ||
          (dwellingCustomer.label && c.name === dwellingCustomer.label)
      );

      if (existingCustomer) {
        handleCustomerSelection(existingCustomer.id.toString());
        setIsCustomerAutoSelected(true);
        setAutoSelectionSource("diagnostic");
      } else {
        const customerFromDiagnostic: Customer = {
          id: dwellingCustomer.id || "diagnostic_customer",
          customer_type: dwellingCustomer.customerType || "individual",
          name:
            dwellingCustomer.label ||
            `Customer ${dwellingCustomer.id?.slice(0, 8) || "Unknown"}`,
          billing_iban: "",
          payment_conditions: dwellingCustomer.paymentConditions || "",
          recipient: "",
          created_at: "",
          updated_at: "",
          phone: dwellingCustomer.phone || "",
          reference: "",
          billing_language: "fr_FR",
          vat_number: dwellingCustomer.vatNumber || "",
          reg_no: dwellingCustomer.regNo || "",
          emails: dwellingCustomer.emails || [],
          billing_address: {
            address: dwellingCustomer.address || "",
            postal_code: dwellingCustomer.postalCode || "",
            city: dwellingCustomer.city || "",
            country_alpha2: "FR",
          },
          delivery_address: {
            address: dwellingCustomer.address || "",
            postal_code: dwellingCustomer.postalCode || "",
            city: dwellingCustomer.city || "",
            country_alpha2: "FR",
          },
          external_reference: dwellingCustomer.sourceId || "",
          ledger_account: { id: 0 },
          notes: null,
          mandates: { url: "" },
        };

        setSelectedCustomerData(customerFromDiagnostic);
        setValue("selectedCustomer", customerFromDiagnostic.id.toString());
        setIsCustomerAutoSelected(true);
        setAutoSelectionSource("diagnostic");
      }
    } else {
      console.log("❌ Auto-selection conditions not met:", {
        hasDwellingAgencyDiagnosticCustomer:
          !!serviceData?.dwelling?.agency?.diagnosticCustomer,
        selectedCustomer,
        isCustomerAutoSelected,
        customersLength: customers.length,
      });
    }
  }, [
    serviceData,
    customers,
    selectedCustomer,
    setValue,
    handleCustomerSelection,
    isCustomerAutoSelected,
  ]);

  const handleAssociateQuote = (data: any) => {
    if (!serviceId || !selectedCustomerData) {
      toast.error("Données manquantes pour l'association");
      return;
    }

    const selectedProductsData = getSelectedProductsData();

    let customerSourceId: string;
    if (
      autoSelectionSource === "diagnostic" &&
      serviceData?.dwelling?.agency?.diagnosticCustomer?.sourceId
    ) {
      // When auto-selected from diagnostic data, use the existing customer's source_id if found
      // Otherwise, use the customer's external_reference or id
      if (selectedCustomerData.external_reference) {
        customerSourceId = selectedCustomerData.external_reference;
      } else {
        customerSourceId = selectedCustomerData.id?.toString();
      }
    } else if (
      isCustomerAutoSelected &&
      selectedQuoteData?.customer?.source_id
    ) {
      customerSourceId = selectedQuoteData.customer.source_id;
    } else {
      customerSourceId =
        selectedCustomerData.external_reference ||
        selectedCustomerData.id?.toString();
    }

    const requestData: AssociateQuoteToServiceRequest = {
      serviceId,
      customerData: {
        source_id: customerSourceId,
        name: selectedCustomerData.name,
        customer_type: selectedCustomerData.customer_type,
        address: selectedCustomerData.billing_address?.address,
        postal_code: selectedCustomerData.billing_address?.postal_code,
        city: selectedCustomerData.billing_address?.city,
        country_alpha2: selectedCustomerData.billing_address?.country_alpha2,
        emails: selectedCustomerData.emails,
        phone: selectedCustomerData.phone,
        reg_no: selectedCustomerData.reg_no,
        vat_number: selectedCustomerData.vat_number,
        recipient: selectedCustomerData.recipient,
        billing_iban: selectedCustomerData.billing_iban || undefined,
        payment_conditions: selectedCustomerData.payment_conditions,
        reference: selectedCustomerData.reference,
        notes: selectedCustomerData.notes || undefined,
      },
    };

    // Add diagnostic customer ID if auto-selected from dwelling data
    if (
      autoSelectionSource === "diagnostic" &&
      serviceData?.dwelling?.agency?.diagnosticCustomer?.id
    ) {
      (requestData as any).diagnosticCustomerId =
        serviceData.dwelling.agency.diagnosticCustomer.id;
    }

    if (data.quoteOption === "existing" && data.selectedQuote) {
      requestData.estimateId = selectedQuoteData?.id || data.selectedQuote;
    } else if (data.quoteOption === "new" && selectedProductsData.length > 0) {
      requestData.line_items = selectedProductsData.map((item) => ({
        label: item.name || "",
        quantity: item.quantity,
        source_id: item.id,
        currency_amount: item.price,
      }));
    } else {
      toast.error("Configuration invalide pour l'association");
      return;
    }

    associateQuote(requestData);
  };

  const handleSubmit = handleSubmitForm((data) => {
    if (!selectedCustomerData) {
      toast.error("Veuillez sélectionner un client");
      return;
    }

    const selectedProductsData = getSelectedProductsData();

    showConfirmDialog({
      data: {
        quoteOption: data.quoteOption as "existing" | "new",
        selectedQuote: data.selectedQuote,
        selectedCustomer: selectedCustomerData,
        selectedProducts: selectedProductsData.map((item) => {
          const productData = products.find((p) => p.id.toString() === item.id);
          return {
            product: productData!,
            quantity: item.quantity,
          };
        }),
        totalAmount: calculateTotal(),
        isCustomerAutoSelected,
      },
      onConfirm: () => handleAssociateQuote(data),
      onCancel: () => {
        console.log("Association cancelled by user");
      },
    });
  });

  const handleQuoteSelection = (_quoteId: string, selectedQuote?: Quote) => {
    setSelectedQuoteData(selectedQuote || null);

    if (selectedQuote?.customer) {
      const quoteCustomer = selectedQuote.customer;
      const existingCustomer =
        customers.find(
          (c) =>
            c.id.toString() === quoteCustomer.source_id ||
            c.name === quoteCustomer.name
        ) ||
        filteredCustomers.find(
          (c) =>
            c.id.toString() === quoteCustomer.source_id ||
            c.name === quoteCustomer.name
        );

      if (existingCustomer) {
        handleCustomerSelection(existingCustomer.id.toString());
        setIsCustomerAutoSelected(true);
        setAutoSelectionSource("quote");
      } else {
        const customerFromQuote: Customer = {
          id: quoteCustomer.v2_id || quoteCustomer.id,
          customer_type: quoteCustomer.customer_type,
          name: quoteCustomer.name,
          billing_iban: quoteCustomer.billing_iban,
          payment_conditions: quoteCustomer.payment_conditions,
          recipient: quoteCustomer.recipient,
          created_at: "",
          updated_at: quoteCustomer.updated_at,
          phone: quoteCustomer.phone,
          reference: quoteCustomer.reference,
          billing_language: "fr_FR",
          vat_number: quoteCustomer.vat_number,
          reg_no: quoteCustomer.reg_no,
          emails: quoteCustomer.emails,
          billing_address: quoteCustomer.billing_address,
          delivery_address: quoteCustomer.delivery_address,
          external_reference: quoteCustomer.source_id || "",
          ledger_account: { id: 0 },
          notes: null,
          mandates: { url: "" },
        };

        setSelectedCustomerData(customerFromQuote);
        setValue("selectedCustomer", customerFromQuote.id.toString());
        setIsCustomerAutoSelected(true);
        setAutoSelectionSource("quote");
      }
    }
  };

  return (
    <div className="p-2  bg-gray-50">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <CurrentOrderCard />

          <QuoteSelectionCard onQuoteSelection={handleQuoteSelection} />
        </div>

        <div className="space-y-6">
          <CustomerSelectionCard
            customerSearchQuery={customerSearchQuery}
            setCustomerSearchQuery={setCustomerSearchQuery}
            loadingCustomers={loadingCustomers}
            filteredCustomers={filteredCustomers}
            selectedCustomer={selectedCustomer}
            customers={customers}
            selectedCustomerData={selectedCustomerData}
            onCustomerSelection={handleCustomerSelection}
            isCustomerAutoSelected={isCustomerAutoSelected}
            autoSelectionSource={autoSelectionSource}
          />

          <OrderSummaryCard
            selectedCustomer={selectedCustomer}
            customers={customers}
            filteredCustomers={filteredCustomers}
            selectedCustomerData={selectedCustomerData}
          />

          <ActionButtons
            onSubmit={handleSubmit}
            disabled={
              !selectedCustomer ||
              (quoteOption === "existing" && !selectedQuote) ||
              (quoteOption === "new" && getSelectedProductsCount() === 0) ||
              isAssociating
            }
            isLoading={isAssociating}
          />
        </div>
      </div>
    </div>
  );
};

const QuoteAssociation: FC<{ serviceData?: any }> = ({ serviceData }) => {
  return (
    <ProductManagementProvider>
      <QuoteAssociationProvider>
        <QuoteAssociationContent serviceData={serviceData} />
      </QuoteAssociationProvider>
    </ProductManagementProvider>
  );
};

export default QuoteAssociation;
